import { Component } from 'react';

import './Project.css';

import { image3 } from '../../theme/images';
import Page from '@acri-tools/react-tools/components/Page/Page';
import request from '@acri-tools/react-tools/utils/request';
import { axiosErrorHandler, isAdmin, nullUndefined, push_history, toast_request_error } from '@acri-tools/react-tools/utils';
import { API_URL, get_page_title } from '../../utils/config';
import LoadingData from '../../components/LoadingData/LoadingData';
import { MONTHS } from '../../utils/dates';
import { ModalClose } from '@acri-tools/react-tools';
import Modal from '@acri-tools/react-tools/components/Modal/Modal';
import UpdateProject from '../../components/UpdateProject/UpdateProject';
import { format_project_data, get_percentage_color, PROJECT_NUMBERS } from '../../utils/projects';
import { Helmet } from 'react-helmet';

interface Props{
    match: any
}
interface State{
    loading: boolean

    update?: any,
    data?: any,
}

class Projects extends Component<Props,State>{

    constructor(props:Props){
        super(props);
        this.state = {
            loading: false,
        }
    }

    componentDidMount(){
        this.fetch(true);
    }

    componentDidUpdate(prevProps:Props){
        if(this.props.match?.params?.name !== prevProps?.match?.params?.name){
            this.fetch();
        }
    }


    fetch = async(load?:boolean) =>{
        if(load) this.setState({ loading: true });
        try {
            let res = await request(`${API_URL}/api/v1/projects/${this.props.match?.params.name}`, {
                method: 'get'
            })


            this.setState({ 
                data: format_project_data(res.data)
            });
        }
        catch (e:any) {
            axiosErrorHandler(e, { functionName: `fetch` });
            toast_request_error(e);
            if(e?.response?.status === 404){
                push_history('/projects')
            }
        }
        finally {
            if(load) this.setState({ loading: false })
        }
    }
    
    get_project_dates = () =>{
        if(!this.state.data) return [];
        switch(this.state.data.status){
            case 'ongoing': 
                return [
                    { name: 'Start date' ,col: 'start_date' },
                    { name: 'Planned start date' ,col: 'planned_start_date' },
                    { name: 'Planned completion date' ,col: 'planned_completion_date' }
                ]
            case 'completed': 
                return [
                    { name: 'Start date' ,col: 'start_date' },
                    { name: 'Planned start date' ,col: 'planned_start_date' },
                    { name: 'Completion date' ,col: 'completion_date' },
                    { name: 'Planned completion date' ,col: 'planned_completion_date' }
                ]
            case 'planned': 
                return [
                    { name: 'Planned start date' ,col: 'planned_start_date' },
                    { name: 'Planned completion date' ,col: 'planned_completion_date' }
                ]
        }
    }

    format_date = (date?:string) =>{
        if(!date) return <div className="no-data">No date</div>
        let d = new Date(date);

        return <>
            <div className="date-year">
                { d.getUTCFullYear() }
            </div>
            <div className="date-date">
                <div className="date-date-month">
                    { MONTHS[d.getUTCMonth()] }
                </div>
                <div className="date-date-day">
                    { d.getUTCDate()?.toString()?.padStart(2, '0') }
                </div>
            </div>
        </>
    }

    render(){
        return (
        <Page 
            id="project"
            banner={{
                title: this.state.data?.name || "project",
                bannerImage: image3,
                topBar: {
                    backTo: '/projects'
                }
            }}
            footer
        >
            <Helmet>
                <title>{ get_page_title(`Project - ${this.state.data?.name || this.props.match?.params?.name}`) }</title>
                <meta 
                    name="description" 
                    content="DAMPS project information"
                />
            </Helmet>
            <div className="page-content">
                <div className="centered-content padding">
                    <div 
                        className="custom-button inline small inverted alt"
                        onClick={()=>{ push_history('/projects') }}
                    >
                        <i className="fas fa-reply"/> PROJECTS
                    </div>
                    <br/>
                    <br/>
                    {
                        isAdmin() &&
                        <>
                            {/* ==============================
                                CREATE 
                                ============================== */}
                            <div 
                                className="custom-button blue inline medium"
                                onClick={()=>{ this.setState({ update: this.state.data }) }}
                            >
                                <i className="fas fa-pen"/> UPDATE PROJECT
                            </div>
                            <br/>
                            <br/>

                            {/* UPDATE */}
                            {
                                this.state.update &&
                                <Modal
                                    className="medium"
                                    opened={!!this.state.update}
                                    close={()=>{ this.setState({ update: undefined }) }}
                                >
                                    <div className="modal-title">
                                        UPDATE PROJECT <ModalClose close={()=>{ this.setState({ update: undefined }) }}/>
                                    </div>
                                    <div className="modal-content">
                                        <UpdateProject
                                            project={this.state.update}
                                            onUpdate={()=>{
                                                this.fetch();
                                                this.setState({ update: undefined });
                                            }}
                                        />
                                    </div>
                                </Modal>
                            }

                        </>
                    }
                    <LoadingData 
                        loading={this.state.loading} no_data={!this.state.data} 
                        no_data_message="No project data"
                    >
                        <div className="project">
                            <div className="project-title">
                                Project Status
                            </div>
                            <div className={`project-status ${this.state.data?.status}`}>
                                { this.state.data?.status }
                            </div>

                            {/* <div className="separator"/> */}

                            <div className="project-dates">
                                {
                                    this.get_project_dates()?.map((date:{ name:string, col: string })=>(
                                        <div className="project-date">
                                            <div className="project-date-name">
                                                { date.name }
                                            </div>
                                            <div className="project-date-value">
                                                { this.format_date(this.state.data?.[date.col]) }
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>

                            <div className="separator"/>
                            {
                                this.state.data && 
                                this.state.data.status !== 'planned' && 
                                PROJECT_NUMBERS?.some((key:string)=> !nullUndefined(this.state.data?.[key])) &&
                                <>
                                    <div className="project-percentages">
                                        {
                                            PROJECT_NUMBERS.map((key:string)=>{
                                                if( nullUndefined(this.state.data?.[key]) )
                                                    return undefined;
                                                return <div className="project-percentage">
                                                    <div className="project-percentage-name">
                                                        { key }
                                                    </div>
                                                    <div className={`project-percentage-value ${get_percentage_color(this.state.data[key])}`}>
                                                        { this.state.data[key] }%
                                                    </div>
                                                </div>
                                            })
                                        }
                                    </div>
        
                                    <div className="separator"/>
                                </>

                            }

                            <div className="project-title">
                                Main Objective
                            </div>
                            <div className="project-objective">
                                { this.state.data?.objective || <div className="no-data">No description</div>  }
                            </div>

                            
                        </div>
                    </LoadingData>
                </div>
            </div>
        </Page>
        );
    }

};


export default Projects;
